<template>
  <el-aside width="220px" style="background-color: #304156; position: relative;">
    <div class="logo">
      <h2>多媒体管理平台</h2>
    </div>
    <el-menu
      :default-active="$route.path"
      class="el-menu-vertical"
      background-color="#304156"
      text-color="#bfcbd9"
      active-text-color="#409EFF"
      router
      :unique-opened="true"
    >
      <el-menu-item index="/dashboard">
        <el-icon><DataAnalysis /></el-icon>
        <span>仪表盘</span>
      </el-menu-item>

      <el-sub-menu index="content-management">
        <template #title>
          <el-icon><Folder /></el-icon>
          <span>内容管理</span>
        </template>
        <el-menu-item index="/packs">
          <el-icon><Picture /></el-icon>
          <span>表情管理</span>
        </el-menu-item>
        <el-menu-item index="/themes" v-if="isAdmin">
          <el-icon><Picture /></el-icon>
          <span>主题管理</span>
        </el-menu-item>
        <!-- 歌单管理二级菜单：所有登录用户可见，类别/标签仅管理员可见 -->
        <el-sub-menu index="playlist-management">
          <template #title>
            <el-icon><List /></el-icon>
            <span>歌单管理</span>
          </template>
          <el-menu-item index="/playlists">
            <span>歌单列表</span>
          </el-menu-item>
          <el-menu-item index="/playlist-categories" v-if="isAdmin">
            <span>歌单类别</span>
          </el-menu-item>
          <el-menu-item index="/playlist-tags" v-if="isAdmin">
            <span>歌单标签</span>
          </el-menu-item>
        </el-sub-menu>
        <el-menu-item index="/songs">
          <el-icon><Headset /></el-icon>
          <span>歌曲管理</span>
        </el-menu-item>
      </el-sub-menu>

      <el-sub-menu index="device-management">
        <template #title>
          <el-icon><Monitor /></el-icon>
          <span>设备管理</span>
        </template>
        <el-menu-item index="/devices">
          <el-icon><Monitor /></el-icon>
          <span>设备列表</span>
        </el-menu-item>
        <el-menu-item index="/board-types" v-if="isAdmin">
          <el-icon><Cpu /></el-icon>
          <span>版型管理</span>
        </el-menu-item>
        <el-menu-item index="/deviceRoleSetting" v-if="isAdmin">
          <el-icon><Setting /></el-icon>
          <div>
            <span>角色配置记录</span>
            <span class="message-badge" v-if="roleSettingMessageNumber > 0">{{roleSettingMessageNumber > 99 ? '99+' : roleSettingMessageNumber}}</span>
          </div>
        </el-menu-item>
        <el-menu-item index="/deviceBoard" v-if="isAdmin">
          <el-icon><ChatLineSquare /></el-icon>
          <span>设备留言板</span>
        </el-menu-item>
        <el-menu-item index="/deviceSchedule" v-if="isAdmin">
          <el-icon><Calendar /></el-icon>
          <span>设备课程表</span>
        </el-menu-item>
      </el-sub-menu>

      <el-sub-menu index="system-management" v-if="isAdmin">
        <template #title>
          <el-icon><Tools /></el-icon>
          <span>系统管理</span>
        </template>
        <el-menu-item index="/users">
          <el-icon><User /></el-icon>
          <span>用户管理</span>
        </el-menu-item>
        <el-menu-item index="/menus">
          <el-icon><Menu /></el-icon>
          <span>小程序菜单</span>
        </el-menu-item>
        <el-menu-item index="/firmwares">
          <el-icon><Cpu /></el-icon>
          <span>固件管理</span>
        </el-menu-item>
        <el-menu-item index="/weather">
          <el-icon><Sunny /></el-icon>
          <span>天气 Key 管理</span>
        </el-menu-item>
        <el-menu-item index="/announcements">
          <el-icon><Bell /></el-icon>
          <span>公告管理</span>
        </el-menu-item>
        <el-menu-item index="/audit">
          <el-icon><Document /></el-icon>
          <span>审计日志</span>
        </el-menu-item>
        <el-menu-item index="/iot-server-api-doc">
          <el-icon><Document /></el-icon>
          <span>IoT API 文档</span>
        </el-menu-item>
      </el-sub-menu>

      <el-menu-item index="/profile">
        <el-icon><UserFilled /></el-icon>
        <span>个人中心</span>
      </el-menu-item>
    </el-menu>
  </el-aside>
</template>

<script>
import { ref, computed, inject, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { 
  DataAnalysis, Monitor, User, List, Headset, Picture, 
  Menu, Sunny, Document, Bell, UserFilled, Cpu,
  Folder, Setting, ChatLineSquare, Calendar, Tools
} from '@element-plus/icons-vue';

export default {
  name: 'LayoutSidebar',
  components: {
    DataAnalysis, Monitor, User, List, Headset, Picture, 
    Menu, Sunny, Document, Bell, UserFilled, Cpu,
    Folder, Setting, ChatLineSquare, Calendar, Tools
  },
  setup() {
    const route = useRoute();
    const userRole = ref(null);
    
    // 更新用户角色
    const updateUserRole = () => {
      try {
        const userStr = localStorage.getItem('user');
        const user = userStr && userStr !== 'undefined' ? JSON.parse(userStr) : {};
        userRole.value = user.role || null;
      } catch (e) {
        userRole.value = null;
      }
    };
    
    const isAdmin = computed(() => {
      return userRole.value === 'admin';
    });

    const currentUser = computed(() => {
      try {
        const userStr = localStorage.getItem('user');
        return userStr && userStr !== 'undefined' ? JSON.parse(userStr) : {};
      } catch (e) {
        return {};
      }
    });
    
    // 监听路由变化，当从登录页跳转时更新用户角色
    watch(() => route.path, (newPath, oldPath) => {
      // 如果从登录页跳转到其他页面，说明刚登录成功，需要更新用户角色
      if (oldPath === '/login' && newPath !== '/login') {
        // 使用 setTimeout 确保 localStorage 已经更新
        setTimeout(() => {
          updateUserRole();
        }, 100);
      }
    }, { immediate: false });
    
    // 监听 localStorage 变化（通过自定义事件，因为同窗口内 storage 事件不会触发）
    const handleStorageChange = () => {
      updateUserRole();
    };
    
    // 使用自定义事件来监听同窗口内的 localStorage 变化
    window.addEventListener('localStorageChange', handleStorageChange);
    
    // 初始化时读取用户角色
    onMounted(() => {
      updateUserRole();
    });

    const initials = computed(() => (currentUser.value.username || 'U').charAt(0).toUpperCase());

    const roleSettingMessageNumber = inject('roleSettingMessageNumber');
    const refreshRoleSettingMessageNumber = inject('refreshRoleSettingMessageNumber');

    // 关闭角色配置数量的定时轮询，仅在管理员身份变化时刷新一次。
    watch(isAdmin, () => {
      if (isAdmin.value) {
        refreshRoleSettingMessageNumber();
      }
    }, { immediate: true });

    onUnmounted(() => {
      window.removeEventListener('localStorageChange', handleStorageChange);
    });
    return { isAdmin, currentUser, initials, roleSettingMessageNumber};
  }
};
</script>

<style scoped>
.logo {
  padding: 20px 16px;
  border-bottom: 1px solid #435266;
  background-color: #2b3a4b;
}
.logo h2 {
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}
.el-menu-vertical {
  border-right: none;
}
.el-menu-vertical :deep(.el-sub-menu__title) {
  height: 50px;
  line-height: 50px;
}
.el-menu-vertical :deep(.el-sub-menu__title:hover) {
  background-color: #263445 !important;
}
.el-menu-vertical .el-menu-item {
  height: 50px;
  line-height: 50px;
  padding: 0 20px;
}
.el-menu-vertical .el-menu-item:hover {
  background-color: #263445 !important;
}
.el-menu-vertical .el-menu-item.is-active {
  background-color: #1890ff !important;
}

.message-badge { 
    background: #f56c6c;
    color: #fff;
    border-radius: 6px;
    padding: 2px 6px;
    margin-left: 10px;
}

.el-menu-item.is-active{
  color:var(--el-menu-text-color);
}
</style>


